import { Loader2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { StageItemCard } from '@/components/discovery/StageItemCard';
import { Progress } from '@/components/ui/progress';
import { useGetStageItemsByStageId } from '@/hooks/stage-items/useGetStageItemsByStageId';
import { useGetStagePrompts } from '@/hooks/stage-prompts/useGetStagePrompts';
import { toast } from '@/hooks/ui/use-toast';
import { getStageItemStatus } from '@/lib/discovery';
import { useStageItemStore } from '@/stores/stages/useStageItemStore';
import { useStageStore } from '@/stores/stages/useStageStore';
import { useStagePromptsStore } from '@/stores/useStagePromptsStore';
import { StageItem } from '@/types/stage.item.type';

export const DashboardDiscovery = () => {
  const { stageId = '' } = useParams();
  const { stageList, discoveryStatus } = useStageStore();
  const { stageItems: stageItemsFromStore, setStageItems } =
    useStageItemStore();
  const { setStagePrompts } = useStagePromptsStore();

  const {
    result: stageItems,
    isError: isStageItemsError,
    isLoading: isStageItemsLoading,
    isSuccess: isStageItemsSuccess,
  } = useGetStageItemsByStageId(stageId);

  const {
    result: stagePromptsResult,
    isError: isStagePromptsError,
    isLoading: isStagePromptsLoading,
    isSuccess: isStagePromptsSuccess,
  } = useGetStagePrompts();

  const currentStage = useMemo(
    () => stageList.find((stage) => stage.id === stageId),
    [stageId, stageList]
  );

  const completedItemsCount = useMemo(
    () =>
      stageItemsFromStore?.filter((item) =>
        discoveryStatus?.completed_stage_item_ids.includes(item.id ?? '')
      ).length || 0,
    [stageItemsFromStore, discoveryStatus]
  );

  const totalItemsCount = stageItemsFromStore?.length || 0;

  const completionPercentage = useMemo(() => {
    if (discoveryStatus?.completed_stage_ids.includes(stageId)) {
      return 100;
    }
    return (completedItemsCount / totalItemsCount) * 100;
  }, [completedItemsCount, totalItemsCount, discoveryStatus, stageId]);

  useEffect(() => {
    if (isStageItemsError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later',
        variant: 'destructive',
      });
    }
  }, [isStageItemsError]);

  useEffect(() => {
    if (isStagePromptsError) {
      toast({
        title: 'Failed to load prompts',
        description: 'Please try again later',
        variant: 'destructive',
      });
    }
  }, [isStagePromptsError]);

  useEffect(() => {
    if (!isStageItemsSuccess || !stageItems?.data) return;

    const sortedStageItems = stageItems.data.reduce<StageItem[]>(
      (acc, curr) => {
        if (curr.previous_stage_item_id !== null) return acc;

        acc.push(curr);
        let nextItemId = curr.id;

        while (nextItemId) {
          const nextItem = stageItems.data.find(
            (item) => item.previous_stage_item_id === nextItemId
          );
          if (!nextItem) break;

          acc.push(nextItem);
          nextItemId = nextItem.id;
        }

        return acc;
      },
      []
    );

    setStageItems(sortedStageItems);
  }, [isStageItemsSuccess, stageItems, setStageItems]);

  useEffect(() => {
    if (isStagePromptsSuccess && stagePromptsResult?.data) {
      setStagePrompts(stagePromptsResult.data);
    }
  }, [isStagePromptsSuccess, stagePromptsResult, setStagePrompts]);

  if (isStageItemsLoading || isStagePromptsLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h5 className="as-strong">{currentStage?.name}</h5>
        <p className="as-title-03 as-strong text-typo-gray-tertiary">
          {`${completedItemsCount} / ${totalItemsCount} completed`}
        </p>
      </div>
      <Progress value={completionPercentage} className="bg-muted" />
      <div className="flex flex-col gap-4 pb-[380px]">
        {stageItemsFromStore?.map((item, index) => (
          <StageItemCard
            key={item.id}
            index={index}
            status={getStageItemStatus(
              index,
              stageId,
              stageItemsFromStore,
              discoveryStatus
            )}
            stageItem={item}
          />
        ))}
      </div>
    </div>
  );
};
